/**
 * Pure helpers for Check-in New list: fetch params, doc mapping, date formatting.
 */
import CustomerConfig from "../../azure/CustomerConfig";
import {
  ONEDRIVE_ROOT,
  SEARCH_VALUE_ENCODING,
  DOCUMENT_MODE,
  ONEDRIVE_FILE_TYPES,
} from "../../redux/constants/metaData";
import {
  getFileName,
  getFileIconPath,
  getFileExtension,
} from "../../functions/helperMethod";
import { getOneDriveDocs } from "../../redux/api/documentsAPI";

/** Page size when expanding selected folders for check-in. */
const FOLDER_EXPAND_PAGE_SIZE = 150;
/** Safety cap on nested folders walked while expanding a selection. */
const MAX_FOLDER_EXPAND_WALK = 50;

/**
 * @param {{ channel?: { id?: string, membershipType?: string, ownerGroupId?: string }, team?: { groupId?: string }, user?: { tenant?: { id?: string } } }} teamsContext
 * @returns {{ groupId: string, channelId: string, fileRoot: string }}
 */
export function resolveGroupId(teamsContext) {
  const channelId = teamsContext?.channel?.id ?? "";
  const groupId =
    teamsContext?.channel != null
      ? teamsContext.channel.membershipType === "Private"
        ? teamsContext.channel.ownerGroupId
        : teamsContext?.team?.groupId
      : "";
  const fileRoot =
    !groupId && !channelId ? ONEDRIVE_ROOT.mainMenu : ONEDRIVE_ROOT.channelDoc;
  return { groupId, channelId, fileRoot };
}

/**
 * @param {string} [searchValue]
 * @returns {string}
 */
export function encodeSearchValue(searchValue) {
  let text = searchValue ?? "";
  if (text && text.includes(SEARCH_VALUE_ENCODING.HASH_SYMBOL)) {
    text = text.replace(
      new RegExp(SEARCH_VALUE_ENCODING.HASH_SYMBOL, "g"),
      SEARCH_VALUE_ENCODING.HASH_ENCODED,
    );
  }
  return text;
}

/** Extensions grouped by OneDrive filter type (matches Working Documents). */
const ONEDRIVE_WORD_EXTENSIONS = new Set([
  "DOC",
  "DOCX",
  "DOCM",
  "DOT",
  "DOTM",
  "DOTX",
  "RTF",
  "ODT",
]);
const ONEDRIVE_EXCEL_EXTENSIONS = new Set([
  "XLS",
  "XLSX",
  "XLSM",
  "CSV",
  "XLSB",
  "ODS",
  "XLT",
  "XLTX",
  "XLTM",
]);
const ONEDRIVE_PPT_EXTENSIONS = new Set([
  "PPT",
  "PPTX",
  "PPTM",
  "POTM",
  "POTX",
  "PPS",
  "PPSX",
  "PPSM",
  "POT",
]);
const ONEDRIVE_PDF_EXTENSIONS = new Set(["PDF"]);

function getAllowedExtensionsForOneDriveFileType(oneDriveFileType) {
  switch (oneDriveFileType) {
    case ONEDRIVE_FILE_TYPES.docx:
      return ONEDRIVE_WORD_EXTENSIONS;
    case ONEDRIVE_FILE_TYPES.xlsx:
      return ONEDRIVE_EXCEL_EXTENSIONS;
    case ONEDRIVE_FILE_TYPES.pptx:
      return ONEDRIVE_PPT_EXTENSIONS;
    case ONEDRIVE_FILE_TYPES.pdf:
      return ONEDRIVE_PDF_EXTENSIONS;
    default:
      return null;
  }
}

/**
 * When search and file-type filter are both active, get-onedrive-docs is queried by
 * search text only; file type is enforced on the client after the response.
 */
export function resolveOneDriveApiFileType(searchValue, oneDriveFileType) {
  const term = (searchValue ?? "").trim();
  const hasFileType =
    Boolean(oneDriveFileType) &&
    oneDriveFileType !== ONEDRIVE_FILE_TYPES.allFiles;

  if (term && hasFileType) {
    return "";
  }
  return oneDriveFileType ?? "";
}

/**
 * Prefer the last executed search; fall back to the current input when the user
 * typed a term but has not clicked Search yet (e.g. filter change after typing).
 */
export function resolveEffectiveSearchValue(appliedSearchValue, searchValue) {
  const applied = (appliedSearchValue ?? "").trim();
  if (applied.length > 0) return applied;
  return (searchValue ?? "").trim();
}

/**
 * @param {object} opts
 * @param {*} opts.teamsContext
 * @param {string} opts.searchValue
 * @param {string} opts.oneDriveSelectedFileType
 * @param {number} opts.pageSize
 * @param {string} [opts.folderId] - Graph drive item id when browsing inside a folder
 * @returns {{ teamId: string, channelId: string, tenantId: string, pageSize: number, searchText: string, documentType: string, fileRoot: string, isOnedrive: boolean, folderId: string }}
 */
export function buildFetchParams({
  teamsContext,
  searchValue,
  oneDriveSelectedFileType,
  pageSize,
  folderId = "",
}) {
  const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
  return {
    teamId: groupId,
    channelId,
    tenantId: teamsContext?.user?.tenant?.id ?? "",
    pageSize: pageSize ?? 20,
    searchText: encodeSearchValue(searchValue),
    documentType: resolveOneDriveApiFileType(
      searchValue,
      oneDriveSelectedFileType,
    ),
    fileRoot,
    isOnedrive: true,
    folderId: folderId ?? "",
  };
}

export function filterDocsByOneDriveFileType(documents, oneDriveFileType) {
  if (!oneDriveFileType || oneDriveFileType === ONEDRIVE_FILE_TYPES.allFiles) {
    return documents ?? [];
  }
  const allowedExtensions =
    getAllowedExtensionsForOneDriveFileType(oneDriveFileType);
  if (!allowedExtensions) return documents ?? [];

  return (documents ?? []).filter((item) => {
    if (isOneDriveFolderItem(item)) return false;
    const ext = getFileExtension(item?.name);
    return allowedExtensions.has(ext?.toUpperCase());
  });
}

/** Always apply the selected file-type filter to displayed rows. */
export function filterDocsForDisplay(documents, { oneDriveFileType }) {
  return filterDocsByOneDriveFileType(documents, oneDriveFileType);
}

export function isOneDriveFileTypeFilterActive(oneDriveFileType) {
  return (
    Boolean(oneDriveFileType) &&
    oneDriveFileType !== ONEDRIVE_FILE_TYPES.allFiles
  );
}

export function getOneDriveClientFilterFetchSize() {
  const config = new CustomerConfig();
  const limit = config.getDataLimit(config.get_OneDriveDataString());
  return limit > 0 ? limit : 150;
}

export function resolveOneDriveListFetchPaging({
  oneDriveFileType,
  pageIndex,
  pageSize = 20,
}) {
  if (isOneDriveFileTypeFilterActive(oneDriveFileType)) {
    return {
      recFrom: 0,
      recSize: getOneDriveClientFilterFetchSize(),
      useClientSidePaging: true,
    };
  }
  return {
    recFrom: pageIndex * pageSize,
    recSize: pageSize,
    useClientSidePaging: false,
  };
}

export function paginateOneDriveDocs(documents, pageIndex, pageSize) {
  const start = pageIndex * pageSize;
  return (documents ?? []).slice(start, start + pageSize);
}

/**
 * @param {string|number|Date} [lastModifiedDateTime]
 * @returns {string}
 */
export function formatModifiedText(lastModifiedDateTime) {
  const parsedDate = parseModifiedDate(lastModifiedDateTime);
  if (!parsedDate) return "";

  const now = new Date();
  const diffInSeconds = Math.floor((now - parsedDate) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);

  const startOfTodayUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  const startOfThatDayUtc = Date.UTC(
    parsedDate.getUTCFullYear(),
    parsedDate.getUTCMonth(),
    parsedDate.getUTCDate(),
  );
  const diffInDays = Math.floor(
    (startOfTodayUtc - startOfThatDayUtc) / (1000 * 60 * 60 * 24),
  );

  if (diffInSeconds < 60) return "A few seconds ago";
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
  }
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  }
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  }

  const monthDayFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
  const fullDateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  if (parsedDate.getUTCFullYear() === now.getUTCFullYear()) {
    return monthDayFormatter.format(parsedDate);
  }
  return fullDateFormatter.format(parsedDate);
}

function parseModifiedDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const text = String(value).trim();
  if (!text) return null;

  if (hasExplicitTimeZone(text)) {
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsedAsUtc = parseTimezoneLessUtcDate(text);
  if (parsedAsUtc) return parsedAsUtc;

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function hasExplicitTimeZone(value) {
  return /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
}

function parseTimezoneLessUtcDate(value) {
  const isoLike = value.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s]+(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(AM|PM)?)?$/i,
  );
  if (isoLike) {
    return buildUtcDate(
      Number(isoLike[1]),
      Number(isoLike[2]),
      Number(isoLike[3]),
      isoLike[4],
      isoLike[5],
      isoLike[6],
      isoLike[7],
    );
  }

  const slashLike = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(AM|PM)?)?$/i,
  );
  if (slashLike) {
    return buildUtcDate(
      Number(slashLike[3]),
      Number(slashLike[1]),
      Number(slashLike[2]),
      slashLike[4],
      slashLike[5],
      slashLike[6],
      slashLike[7],
    );
  }

  return null;
}

function buildUtcDate(year, month, day, hourValue, minuteValue, secondValue, meridiem) {
  let hour = Number(hourValue ?? 0);
  const minute = Number(minuteValue ?? 0);
  const second = Number(secondValue ?? 0);

  if ([year, month, day, hour, minute, second].some(Number.isNaN)) {
    return null;
  }

  if (meridiem) {
    const period = meridiem.toUpperCase();
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour < 12) hour += 12;
  }

  const parsed = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day ||
    parsed.getUTCHours() !== hour ||
    parsed.getUTCMinutes() !== minute ||
    parsed.getUTCSeconds() !== second
  ) {
    return null;
  }

  return parsed;
}

function getUtcOffsetLabel(date) {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const minutes = String(absMinutes % 60).padStart(2, "0");
  return `UTC${sign}${hours}:${minutes}`;
}

export function formatModifiedTooltipText(lastModifiedDateTime) {
  const parsedDate = parseModifiedDate(lastModifiedDateTime);
  if (!parsedDate) return "";

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateTimeText = new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(parsedDate);

  const zoneNameParts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "long",
  }).formatToParts(parsedDate);
  const zoneName =
    zoneNameParts.find((part) => part.type === "timeZoneName")?.value ||
    timeZone;

  return `${dateTimeText} (${getUtcOffsetLabel(parsedDate)})`;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return "";
}

function getMetaDataObject(item) {
  const rawMetaData = item?.MetaData ?? item?.metaData ?? item?.metadata;
  if (!rawMetaData) return null;
  if (typeof rawMetaData === "object") return rawMetaData;
  if (typeof rawMetaData !== "string") return null;
  try {
    return JSON.parse(rawMetaData);
  } catch {
    return null;
  }
}

function parseIfsIdentifiersFromFileName(fileName) {
  if (!fileName) return {};
  const openParen = fileName.lastIndexOf("(");
  const closeParen = fileName.indexOf(")", openParen >= 0 ? openParen : 0);
  if (openParen < 0 || closeParen <= openParen) return {};
  const inside = fileName.slice(openParen + 1, closeParen).trim();
  if (!inside) return {};
  const parts = inside.split(" - ");
  if (parts.length < 4) return {};
  return {
    docClass: parts[0]?.trim() ?? "",
    docNo: parts[1]?.trim() ?? "",
    docSheet: parts[2]?.trim() ?? "",
    docRev: parts[3]?.trim() ?? "",
  };
}

function getDocIdentifiers(item) {
  const metaData = getMetaDataObject(item) ?? {};
  const fromName = parseIfsIdentifiersFromFileName(item?.name ?? "");
  return {
    docClass: firstNonEmpty(
      item?.DocClass,
      item?.docClass,
      metaData?.DocClass,
      metaData?.docClass,
      fromName?.docClass,
    ),
    docNo: firstNonEmpty(
      item?.DocNo,
      item?.docNo,
      metaData?.DocNo,
      metaData?.docNo,
      fromName?.docNo,
    ),
    docSheet: firstNonEmpty(
      item?.DocSheet,
      item?.docSheet,
      metaData?.DocSheet,
      metaData?.docSheet,
      fromName?.docSheet,
    ),
    docRev: firstNonEmpty(
      item?.DocRev,
      item?.docRev,
      metaData?.DocRev,
      metaData?.docRev,
      fromName?.docRev,
    ),
    formatSize: firstNonEmpty(
      item?.FormatSize,
      item?.formatSize,
      metaData?.FormatSize,
      metaData?.formatSize,
    ),
  };
}

/**
 * @param {Array<{ id?: string, name?: string, file?: { mimeType?: string }, fileState?: string, status?: string, lastModifiedDateTime?: string, lastModifiedBy?: { user?: { displayName?: string } }, microsoftGraphDownloadUrl?: string, webUrl?: string }>} documents
 * @returns {Array<{ key: string, iconName: string, name: string, title: string, modified: string, modifiedTooltip: string, modifiedby: string, downloadlink?: string, fileType: string, weblink?: string, webLinkUrl?: string, documentMode: string }>}
 */
export function isOneDriveFolderItem(item) {
  return item?.folder != null && item.folder !== undefined;
}

/**
 * Fetches all drive items in a OneDrive folder (paged).
 * @param {string} accessToken
 * @param {{ teamId?: string, channelId?: string, tenantId?: string, oneDriveRoot?: string }} listParams
 * @param {string} folderId
 * @returns {Promise<object[]>}
 */
export async function fetchAllDocsInOneDriveFolder(
  accessToken,
  listParams,
  folderId,
) {
  const all = [];
  let recFrom = 0;
  let totalCount = Number.POSITIVE_INFINITY;

  while (recFrom < totalCount) {
    const { data, status } = await getOneDriveDocs(accessToken, {
      teamId: listParams?.teamId ?? "",
      channelId: listParams?.channelId ?? "",
      tenantId: listParams?.tenantId ?? "",
      oneDriveRoot: listParams?.oneDriveRoot ?? "",
      isOnedrive: true,
      folderId: folderId ?? "",
      searchText: "",
      fileType: "",
      recFrom,
      recSize: FOLDER_EXPAND_PAGE_SIZE,
    });

    if (status === 204) {
      break;
    }
    if (status !== 200) {
      throw new Error(
        data?.errorMessage ?? `Failed to load folder contents (${status})`,
      );
    }

    const page = Array.isArray(data?.value) ? data.value : [];
    totalCount =
      typeof data?.totalCount === "number"
        ? data.totalCount
        : recFrom + page.length;
    all.push(...page);

    if (page.length === 0) {
      break;
    }
    recFrom += page.length;
    if (page.length < FOLDER_EXPAND_PAGE_SIZE) {
      break;
    }
  }

  return all;
}

/**
 * Builds the document list for check-in from a selection that may include folders.
 * Directly selected files are kept; selected folders are expanded (including nested folders).
 * @param {Array<object>} selectedItems - table row items (`isFolder`, `key`, …)
 * @param {{ accessToken: string, teamId?: string, channelId?: string, tenantId?: string, oneDriveRoot?: string }} options
 * @returns {Promise<object[]>} mapped non-folder check-in items, deduped by key
 */
export async function collectCheckInDocumentsFromSelection(
  selectedItems,
  { accessToken, teamId, channelId, tenantId, oneDriveRoot },
) {
  const selected = Array.isArray(selectedItems) ? selectedItems : [];
  const byKey = new Map();

  for (const item of selected) {
    if (item?.isFolder === true) continue;
    const key = String(item?.key ?? "");
    if (key) {
      byKey.set(key, item);
    }
  }

  const folderIds = selected
    .filter((item) => item?.isFolder === true && item?.key)
    .map((item) => String(item.key));

  if (!folderIds.length) {
    return Array.from(byKey.values());
  }

  const listParams = {
    teamId: teamId ?? "",
    channelId: channelId ?? "",
    tenantId: tenantId ?? "",
    oneDriveRoot: oneDriveRoot ?? "",
  };

  const foldersToVisit = [...folderIds];
  const visitedFolders = new Set();

  while (
    foldersToVisit.length > 0 &&
    visitedFolders.size < MAX_FOLDER_EXPAND_WALK
  ) {
    const folderId = foldersToVisit.shift();
    if (!folderId || visitedFolders.has(folderId)) continue;
    visitedFolders.add(folderId);

    const children = await fetchAllDocsInOneDriveFolder(
      accessToken,
      listParams,
      folderId,
    );

    for (const child of children) {
      if (isOneDriveFolderItem(child)) {
        const childId = child?.id != null ? String(child.id) : "";
        if (childId && !visitedFolders.has(childId)) {
          foldersToVisit.push(childId);
        }
        continue;
      }
      const [mapped] = mapDocsToItems([child]);
      if (mapped?.key) {
        byKey.set(String(mapped.key), mapped);
      }
    }
  }

  return Array.from(byKey.values());
}

const BROWSE_SUPPORTED_EXTENSIONS = new Set([
  "DOC",
  "DOCX",
  "DOCM",
  "DOT",
  "DOTM",
  "DOTX",
  "RTF",
  "ODT",
  "XLS",
  "XLSX",
  "XLSM",
  "CSV",
  "XLSB",
  "ODS",
  "XLT",
  "XLTX",
  "XLTM",
  "PPT",
  "PPTX",
  "PPTM",
  "POTM",
  "POTX",
  "PPS",
  "PPSX",
  "PPSM",
  "POT",
  "PDF",
]);

/** @type {Set<string> | null} */
let browseSupportedExtensionsFromApi = null;

/**
 * @param {Array<{ FileExtention?: string, fileExtention?: string }>} fileTypes
 * @returns {boolean} true when API extensions were applied
 */
export function setBrowseSupportedExtensionsFromApi(fileTypes) {
  if (!Array.isArray(fileTypes) || fileTypes.length === 0) {
    return false;
  }

  const extensions = new Set();
  for (const item of fileTypes) {
    const ext = String(item?.FileExtention ?? item?.fileExtention ?? "").trim();
    if (!ext) continue;
    extensions.add(ext.replace(/^\./, "").toUpperCase());
  }

  if (extensions.size === 0) {
    return false;
  }

  browseSupportedExtensionsFromApi = extensions;
  return true;
}

export function getBrowseSupportedExtensions() {
  return browseSupportedExtensionsFromApi ?? BROWSE_SUPPORTED_EXTENSIONS;
}

export function getBrowseAcceptExtensions() {
  return Array.from(getBrowseSupportedExtensions()).map(
    (ext) => `.${ext.toLowerCase()}`,
  );
}

export function getBrowseAcceptAttribute() {
  return getBrowseAcceptExtensions().join(",");
}

export function isSupportedBrowseFile(file) {
  if (!file) return false;
  const ext = getFileExtension(file?.name ?? "");
  return Boolean(ext && getBrowseSupportedExtensions().has(ext.toUpperCase()));
}

/**
 * Normalize browsed files like Share: keep the raw File/Blob for chunked upload.
 * Do not convert large files to base64 on select (FileReader fails / OOMs).
 */
export async function normalizeBrowseFiles(files) {
  const list = Array.isArray(files) ? files : [];
  const normalized = [];
  for (let index = 0; index < list.length; index += 1) {
    const file = list[index];
    if (!isSupportedBrowseFile(file)) {
      continue;
    }
    const fileName = file?.name ?? "";
    const path =
      typeof file?.webkitRelativePath === "string" && file.webkitRelativePath
        ? file.webkitRelativePath
        : fileName;
    normalized.push({
      id: `${fileName}-${index}-${file?.size ?? 0}`,
      name: fileName,
      path,
      size: typeof file?.size === "number" ? file.size : 0,
      type: file?.type || getFileExtension(fileName) || "",
      source: "browse",
      // Share-style: defer encoding to chunked binary upload at check-in.
      base64str: "",
      file,
    });
  }
  return normalized;
}

export function mapDocsToItems(documents) {
  if (!documents?.length) return [];
  return documents.map((item) => {
    const isFolder = isOneDriveFolderItem(item);
    const docIdentifiers = getDocIdentifiers(item);
    const fileExtension =
      !isFolder && item?.name && item.name.includes(".")
        ? item.name.split(".").pop()
        : "";

    const iconPath = isFolder
      ? null
      : getFileIconPath(item.file?.mimeType, {
          FileExtension: fileExtension,
          FileType: item.file?.mimeType,
          // Let the icon vary for released/checked-out variants when backend provides it.
          FileState: item.fileState ?? "",
          Status: item.status ?? "",
        });
    const webUrl = item.webUrl;
    return {
      key: item.id,
      id: item.id,
      iconName: iconPath,
      isFolder,
      name: item.name,
      title: isFolder ? item.name : getFileName(item.name),
      modified: formatModifiedText(item.lastModifiedDateTime),
      modifiedTooltip: formatModifiedTooltipText(item.lastModifiedDateTime),
      modifiedby: item.lastModifiedBy?.user?.displayName ?? "",
      downloadlink: item.microsoftGraphDownloadUrl,
      fileType: item.file?.mimeType ?? "",
      weblink: webUrl,
      webLinkUrl: webUrl,
      documentMode: DOCUMENT_MODE.oneDrive,
      docNo: docIdentifiers.docNo,
      docRev: docIdentifiers.docRev,
      docClass: docIdentifiers.docClass,
      docSheet: docIdentifiers.docSheet,
      formatSize: docIdentifiers.formatSize,
      fileState: item.fileState ?? "",
      metaData: item.MetaData ?? item.metaData ?? item.metadata ?? "",
      driveId: item.DriveId ?? item.driveId ?? "",
      itemsId: item.ItemsId ?? item.itemsId ?? "",
      folderPath: Array.isArray(item.folderPath) ? item.folderPath : null,
    };
  });
}
